import React, { useState } from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import { Modal, Panel, PanelGroup, designSystem } from '@yola/ws-ui';
import segment from 'src/js/modules/analytics/segment';
import Breadcrumbs from 'src/js/modules/common/components/breadcrumbs';
import SearchBar from './search-bar';
import DesignOtherStylesOption from './design-other-styles-option';
import EmptyState from './empty-state';
import constants from '../constants';
import optionIds from '../constants/option-ids';

const {
  Stack,
  Box,
  DialogHeader,
  DialogButtonGroup,
  DialogButton,
  DialogButtonGroupSeparator,
  Heading,
  Paragraph,
  Accordion,
  AccordionGroup,
  AccordionHeader,
  AccordionContent,
} = designSystem;

const { DEFAULT_OFFSET_Y, DEFAULT_OFFSET_X, DEFAULT_DIALOG_HEIGHT, DEFAULT_DIALOG_WIDTH } =
  constants;

const {
  trackers: { trackEvent },
  constants: { events },
} = segment;

const DIALOG_MIN_HEIGHT = 255;

const filterSections = (sections, query) => {
  if (!query.trim()) {
    return sections;
  }

  const normalizedQuery = query.toLowerCase().trim();

  return sections
    .map((section) => {
      const normalizedTitle = section.title.toLowerCase().trim();

      if (normalizedTitle.includes(normalizedQuery)) {
        return section;
      }

      const filteredOptions = section.options.filter((option) => {
        const normalizedOptionTitle = option.title.toLowerCase().trim();
        return normalizedOptionTitle.includes(normalizedQuery);
      });

      if (filteredOptions.length) {
        return {
          ...section,
          options: filteredOptions,
        };
      }

      return null;
    })
    .filter(Boolean);
};

const DesignOtherStylesDialog = ({ captions, sections, onCancel, onSubmit }) => {
  const [filteredSections, setFilteredSections] = useState(sections);

  const handleSearchChange = (query) => {
    if (query.trim()) {
      trackEvent(events.DESIGN_SETTINGS_SEARCH_PERFORMED, {
        sourceId: optionIds.OTHER_STYLES,
        searchTerm: query,
      });
    }
    setFilteredSections(filterSections(sections, query));
  };

  const handleSearchClear = () => {
    setFilteredSections(sections);
  };

  const handleSearchFocus = () => {
    trackEvent(events.DESIGN_SETTINGS_SEARCH_CLICKED, {
      sourceId: optionIds.OTHER_STYLES,
    });
  };

  const defaultActiveItem = filteredSections.map((section) => section.id);
  const isNotEmpty = filteredSections.length > 0;

  return (
    <Modal
      draggable
      resizable
      fullscreen={bowser.mobile}
      width={DEFAULT_DIALOG_WIDTH}
      minWidth={DEFAULT_DIALOG_WIDTH}
      maxWidth={DEFAULT_DIALOG_WIDTH}
      height={DEFAULT_DIALOG_HEIGHT}
      minHeight={DIALOG_MIN_HEIGHT}
      offsetX={DEFAULT_OFFSET_X}
      offsetY={DEFAULT_OFFSET_Y}
      dragHandleSelector=".ws-drag-trigger"
      dragCancelSelector=".ws-ui-dialog-header-controls"
      className="ws-design-other-styles"
      overlay="none"
      resizeIcon="ns-resize"
      handleCancel={onCancel}
      handleSubmit={onSubmit}
      onOverlayClick={onSubmit}
    >
      <PanelGroup height="100%">
        <Panel
          align="middle"
          corners="squared"
          className="ws-drag-trigger"
          style={{ flexShrink: '0' }}
        >
          <DialogHeader paddingBottom="spacing-s">
            <Box>
              <Box paddingBottom="spacing-2-xs">
                <Breadcrumbs breadcrumbs={captions.breadcrumbs} />
              </Box>
              <Box>
                <Stack>
                  <Heading type="heading-3" singleLine>
                    {captions.title}
                  </Heading>
                  <Paragraph>{captions.description}</Paragraph>
                </Stack>
              </Box>
            </Box>
          </DialogHeader>
        </Panel>
        <Panel style={{ borderBottom: 'none', flexShrink: '0' }}>
          <SearchBar
            placeholder={captions.searchPlaceholder}
            onChange={handleSearchChange}
            onClear={handleSearchClear}
            onFocus={handleSearchFocus}
          />
        </Panel>
        <Panel height="100%" scrollable style={{ borderBottom: 'none' }}>
          <div className="ws-design-other-styles__container">
            {isNotEmpty ? (
              <Accordion appearance="grey" defaultActiveItem={defaultActiveItem}>
                {filteredSections.map((section) => (
                  <AccordionGroup key={section.id} itemId={section.id}>
                    <AccordionHeader isSticky title={section.title.toUpperCase()} />
                    <AccordionContent>
                      <Box
                        paddingTop="spacing-2-xs"
                        paddingBottom="spacing-2-xs"
                        paddingLeft="spacing-m"
                        paddingRight="spacing-m"
                      >
                        {section.options.map(({ id, title, options }) => (
                          <Box paddingTop="spacing-2-xs" paddingBottom="spacing-2-xs" key={id}>
                            <Stack gap="spacing-2-xs">
                              <Paragraph isClipped>{title}</Paragraph>
                              {options.map((option) => (
                                <DesignOtherStylesOption key={option.id} option={option} />
                              ))}
                            </Stack>
                          </Box>
                        ))}
                      </Box>
                    </AccordionContent>
                  </AccordionGroup>
                ))}
              </Accordion>
            ) : (
              <EmptyState title={captions.emptyStateTitle} subtitle={captions.emptyStateSubtitle} />
            )}
          </div>
        </Panel>
        <Panel corners="squared" style={{ flexShrink: '0' }}>
          <DialogButtonGroup>
            <DialogButton onClick={onSubmit}>{captions.submitButton}</DialogButton>
            <DialogButtonGroupSeparator />
            <DialogButton onClick={onCancel}>{captions.cancelButton}</DialogButton>
          </DialogButtonGroup>
        </Panel>
      </PanelGroup>
    </Modal>
  );
};

DesignOtherStylesDialog.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  captions: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    breadcrumbs: PropTypes.arrayOf(PropTypes.string),
    searchPlaceholder: PropTypes.string,
    emptyStateTitle: PropTypes.string,
    emptyStateSubtitle: PropTypes.string,
    submitButton: PropTypes.string,
    cancelButton: PropTypes.string,
  }).isRequired,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          title: PropTypes.string.isRequired,
          options: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.string.isRequired,
              type: PropTypes.string.isRequired,
              title: PropTypes.string,
            })
          ),
        })
      ),
    })
  ).isRequired,
};

export default DesignOtherStylesDialog;
